import { z } from 'zod';

export const ActionItemStatusSchema = z.enum(['To Do', 'In Progress', 'Done']);
export type ActionItemStatus = z.infer<typeof ActionItemStatusSchema>;

export const ACTION_ITEM_STATUS = {
  TODO: 'To Do' as const,
  IN_PROGRESS: 'In Progress' as const,
  DONE: 'Done' as const,
} satisfies Record<string, ActionItemStatus>;

export const ACTION_ITEM_STATUS_VALUES = Object.values(ACTION_ITEM_STATUS);

// ===== workspace-wide page property schema =====
export const TagOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
});

export type TagOption = z.infer<typeof TagOptionSchema>;

export const ActionItemPrioritySchema = z.enum(['High', 'Medium', 'Low']);
export type ActionItemPriority = z.infer<typeof ActionItemPrioritySchema>;

export type ActionItemPriorityMeta = {
  label: ActionItemPriority;
  color: string;
  weight: number;
};

export const ACTION_ITEM_PRIORITY_META: Readonly<
  Record<ActionItemPriority, ActionItemPriorityMeta>
> = {
  High: {
    label: 'High',
    color: 'var(--pique-priority-high, #E5484D)',
    weight: 0,
  },
  Medium: {
    label: 'Medium',
    color: 'var(--pique-priority-medium, #F5A623)',
    weight: 1,
  },
  Low: { label: 'Low', color: 'var(--pique-priority-low, #30A46C)', weight: 2 },
} as const;

export const PRIORITY_ORDER: Readonly<ActionItemPriority[]> = [
  'High',
  'Medium',
  'Low',
] as const;

export const PRIORITY_WEIGHT: Readonly<Record<ActionItemPriority, number>> = {
  High: 0,
  Medium: 1,
  Low: 2,
} as const;

export function getPriorityMeta(
  priority: ActionItemPriority
): ActionItemPriorityMeta {
  return ACTION_ITEM_PRIORITY_META[priority];
}

export function priorityWeight(priority: ActionItemPriority): number {
  return PRIORITY_WEIGHT[priority];
}

export function priorityColor(priority: ActionItemPriority): string {
  return ACTION_ITEM_PRIORITY_META[priority].color;
}

export const FollowupEmailStatusSchema = z.enum([
  'NotStarted',
  'Draft',
  'Copied',
]);
export type FollowupEmailStatus = z.infer<typeof FollowupEmailStatusSchema>;

export const AugmentationStatusSchema = z.enum([
  'NotStarted',
  'Original',
  'Augmented',
]);
export type AugmentationStatus = z.infer<typeof AugmentationStatusSchema>;

export enum PageSystemPropertyId {
  Tags = 'tags',
  Journal = 'journal',
  ActionItems = 'actionItems',
  Attendees = 'attendees',
  FollowupEmailStatus = 'followupEmailStatus',
  AugmentationStatus = 'augmentationStatus',
  Overview = 'overview',
  SocialFeed = 'socialFeed',
  MeetingPrepSummary = 'meetingPrepSummary',
  PendingAugmentation = 'pendingAugmentation',
}

export enum PagePropertyType {
  Text = 'text',
  Number = 'number',
  Date = 'date',
  Progress = 'progress',
  Checkbox = 'checkbox',
  Tags = 'tags',
  Members = 'members',
  Attendees = 'attendees',
}

export const PagePropertyMetaBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  source: z.string(),
  type: z.nativeEnum(PagePropertyType),
  icon: z.string(),
  required: z.boolean().optional(),
});

export const PageSystemPropertyMetaBaseSchema =
  PagePropertyMetaBaseSchema.extend({
    source: z.literal('system'),
  });

export const BreakdownStepSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  completed: z.boolean().default(false),
});

// Schema for meeting attendees
export const AttendeeSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().email(),
  avatar: z.string().optional(),
  role: z.string().optional(),
  organization: z.string().optional(),
  notes: z.string().optional(),
  present: z.boolean().default(true),
  joinedAt: z.union([z.string(), z.null()]).optional(),
  responseStatus: z.string().optional(),
  organizer: z.boolean().optional(),
  self: z.boolean().optional(),
});

// Schema for todo/action items
export const TodoItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  pageId: z.string(), // Empty string for orphaned items
  pageTitle: z.string(),
  databaseId: z.string(),
  columnId: z.string(),
  createdAt: z.union([z.date(), z.null()]),
  status: ActionItemStatusSchema.optional(),
  priority: ActionItemPrioritySchema.default('Medium'),
  priorityRank: z.number().int().min(0).optional(),
  progress: z.number().min(0).max(100).optional(),
  assignedTo: z.array(AttendeeSchema).optional(),
  remindLater: z.array(z.string()).optional(),
  dueDate: z.union([z.date(), z.null(), z.string()]).optional(),
  closeLoop: z.string().optional(),
  context: z.string().optional(),
  completedAt: z.union([z.string(), z.null()]).optional(),
  deletedAt: z.union([z.string(), z.null()]).optional(),
  isFocused: z.boolean().default(false).optional(),
  focusTimeSeconds: z.number().int().min(0).default(0).optional(),
  focusDaily: z.record(z.number()).optional(),
  scheduledAt: z.string().optional(),
  plannedDuration: z.number().int().min(1).optional(),
  repeatConfig: z
    .object({
      frequency: z.enum(['daily', 'weekdays', 'weekly']),
      time: z.string(),
    })
    .optional(),
  completedDates: z.record(z.boolean()).optional(),
  followupReminder: z
    .object({
      enabled: z.boolean().default(false).optional(),
      minutes: z.number().int().min(1).max(1440).optional(),
      scheduleAt: z.union([z.string(), z.date()]).optional(),
      message: z.string().optional(),
      channels: z
        .object({
          in_app: z.boolean().optional(),
          push: z.boolean().optional(),
          email: z.boolean().optional(),
          sms: z.boolean().optional(),
          chat: z.boolean().optional(),
        })
        .optional(),
    })
    .optional(),
  reminderState: z
    .object({
      isSet: z.boolean().default(false),
      scheduledAt: z.string().optional(),
      minutes: z.number().int().min(1).optional(),
      transactionId: z.string().optional(),
    })
    .optional(),
  breakdownSteps: z.array(BreakdownStepSchema).optional(),
  aiTags: z.array(z.string()).optional(),
});

export const ActionItemsValueSchema = z.array(TodoItemSchema);

export const AttendeesValueSchema = z.array(AttendeeSchema);

// Schema for transcription chunks
export const TranscriptionChunkSchema = z.object({
  id: z.string(),
  text: z.string(),
  timestampMs: z.number(),
  speaker: z.string().optional(),
  confidence: z.number().optional(),
  vendor: z.enum(['WebSocket', 'Deepgram', 'AwsTranscribe']).optional(),
  isFinal: z.boolean().default(true),
});

export const TranscriptionChunkInputSchema = TranscriptionChunkSchema.omit({
  id: true,
});

// Incremental summary chunk (each item is stored independently like transcript chunks)
export const SummaryChunkSchema = z.object({
  id: z.string(),
  text: z.string(),
  timestampMs: z.number(),
  speaker: z.string().optional(),
  confidence: z.number().optional(),
  vendor: z.enum(['WebSocket', 'Deepgram', 'AwsTranscribe']).optional(),
  isFinal: z.boolean().default(true),
});

export const SocialFeedItemSchema = z.object({
  id: z.string(),
  attendeeEmail: z.string(),
  attendeeName: z.string().default(''),
  text: z.string(),
  confidence: z.number().min(0).max(1),
  source: z.string().optional(),
  timestamp: z.string().optional(),
});
export const SocialFeedValueSchema = z.array(SocialFeedItemSchema);

export const AttendeeBlurbSchema = z.object({
  email: z.string(),
  name: z.string(),
  confident: z.boolean(),
  blurb: z.string().optional(),
  keyPoints: z.array(z.string()).optional(),
  company: z.string().optional(),
  role: z.string().optional(),
});

export type AttendeeBlurb = z.infer<typeof AttendeeBlurbSchema>;

export const MeetingPrepSummaryValueSchema = z.object({
  briefSummary: z.string().default(''),
  topicsLikelyToComeUp: z.array(z.string()).default([]),
  selectedAttendeeEmails: z.array(z.string()).default([]),
  sourceDocIds: z.array(z.string()).default([]),
  generatedAt: z.string().optional(),
  attendeeBlurbs: z.array(AttendeeBlurbSchema).optional(),
});

export interface PendingMindmapNode {
  text: string;
  children: PendingMindmapNode[];
}

export const PendingAugmentationValueSchema = z.object({
  meetingTitle: z.string().optional(),
  meetingOverview: z.string().optional(),
  transcript: z.string().optional(),
  mindmapTree: z.unknown().nullable().optional(),
  createdAt: z.string().optional(),
});
export type PendingAugmentationValue = z.infer<typeof PendingAugmentationValueSchema>;

export const PageCustomPropertyMetaSchema = PagePropertyMetaBaseSchema.extend({
  source: z.literal('custom'),
  order: z.number(),
});

export const PageInfoItemSchema = z.object({
  id: z.string(), // property id. Maps to PagePropertyMetaSchema.id
  visibility: z.enum(['visible', 'hide', 'hide-if-empty']),
  value: z.any(), // corresponds to PagePropertyMetaSchema.type
});

export const PageInfoJournalItemSchema = PageInfoItemSchema.extend({
  id: z.literal(PageSystemPropertyId.Journal),
  value: z.union([z.string(), z.literal(false)]),
});

export const PageInfoTagsItemSchema = PageInfoItemSchema.extend({
  id: z.literal(PageSystemPropertyId.Tags),
  value: z.array(z.string()),
});

export type PageInfoTagsItem = z.infer<typeof PageInfoTagsItemSchema>;

// ====== workspace properties schema ======
export const WorkspaceFavoriteItemSchema = z.object({
  id: z.string(),
  order: z.string(),
  type: z.enum(['doc', 'collection']),
  value: z.boolean(),
});

export type WorkspaceFavoriteItem = z.infer<typeof WorkspaceFavoriteItemSchema>;

const WorkspaceAffinePropertiesSchemaSchema = z.object({
  pageProperties: z.object({
    custom: z.record(PageCustomPropertyMetaSchema),
    system: z.object({
      [PageSystemPropertyId.Journal]: PageSystemPropertyMetaBaseSchema.extend({
        id: z.literal(PageSystemPropertyId.Journal),
        type: z.literal(PagePropertyType.Date),
      }),
      [PageSystemPropertyId.Tags]: PagePropertyMetaBaseSchema.extend({
        id: z.literal(PageSystemPropertyId.Tags),
        type: z.literal(PagePropertyType.Tags),
        options: z.array(TagOptionSchema),
      }),
      [PageSystemPropertyId.ActionItems]:
        PageSystemPropertyMetaBaseSchema.extend({
          id: z.literal(PageSystemPropertyId.ActionItems),
          type: z.literal(PagePropertyType.Text),
        }),
      [PageSystemPropertyId.Attendees]: PagePropertyMetaBaseSchema.extend({
        id: z.literal(PageSystemPropertyId.Attendees),
        type: z.literal(PagePropertyType.Attendees),
      }),
      [PageSystemPropertyId.Overview]: PagePropertyMetaBaseSchema.extend({
        id: z.literal(PageSystemPropertyId.Overview),
        type: z.literal(PagePropertyType.Text),
      }),
      [PageSystemPropertyId.FollowupEmailStatus]:
        PageSystemPropertyMetaBaseSchema.extend({
          id: z.literal(PageSystemPropertyId.FollowupEmailStatus),
          type: z.literal(PagePropertyType.Text),
        }),
      [PageSystemPropertyId.AugmentationStatus]:
        PageSystemPropertyMetaBaseSchema.extend({
          id: z.literal(PageSystemPropertyId.AugmentationStatus),
          type: z.literal(PagePropertyType.Text),
        }),
      [PageSystemPropertyId.SocialFeed]:
        PageSystemPropertyMetaBaseSchema.extend({
          id: z.literal(PageSystemPropertyId.SocialFeed),
          type: z.literal(PagePropertyType.Text),
        }),
      [PageSystemPropertyId.PendingAugmentation]:
        PageSystemPropertyMetaBaseSchema.extend({
          id: z.literal(PageSystemPropertyId.PendingAugmentation),
          type: z.literal(PagePropertyType.Text),
        }),
    }),
  }),
});

const PageInfoCustomPropertyItemSchema = PageInfoItemSchema.extend({
  order: z.string(),
});

export const PageInfoActionItemsItemSchema = PageInfoItemSchema.extend({
  id: z.literal(PageSystemPropertyId.ActionItems),
  value: z.unknown(),
});

export const PageInfoAttendeesItemSchema = PageInfoItemSchema.extend({
  id: z.literal(PageSystemPropertyId.Attendees),
  value: AttendeesValueSchema,
});

export const PageInfoFollowupEmailStatusItemSchema = PageInfoItemSchema.extend({
  id: z.literal(PageSystemPropertyId.FollowupEmailStatus),
  value: FollowupEmailStatusSchema,
});

export const PageInfoAugmentationStatusItemSchema = PageInfoItemSchema.extend({
  id: z.literal(PageSystemPropertyId.AugmentationStatus),
  value: AugmentationStatusSchema,
});

export const PageInfoOverviewItemSchema = PageInfoItemSchema.extend({
  id: z.literal(PageSystemPropertyId.Overview),
  value: z.string().default(''),
});

export const PageInfoSocialFeedItemSchema = PageInfoItemSchema.extend({
  id: z.literal(PageSystemPropertyId.SocialFeed),
  value: z.unknown(),
});

export const PageInfoMeetingPrepSummaryItemSchema = PageInfoItemSchema.extend({
  id: z.literal(PageSystemPropertyId.MeetingPrepSummary),
  value: MeetingPrepSummaryValueSchema,
});

export const PageInfoPendingAugmentationItemSchema = PageInfoItemSchema.extend({
  id: z.literal(PageSystemPropertyId.PendingAugmentation),
  value: PendingAugmentationValueSchema.nullable(),
});

const WorkspacePagePropertiesSchema = z.object({
  custom: z.record(PageInfoCustomPropertyItemSchema),
  system: z.object({
    [PageSystemPropertyId.Journal]: PageInfoJournalItemSchema,
    [PageSystemPropertyId.Tags]: PageInfoTagsItemSchema,
    [PageSystemPropertyId.ActionItems]: PageInfoActionItemsItemSchema,
    [PageSystemPropertyId.Attendees]: PageInfoAttendeesItemSchema,
    [PageSystemPropertyId.Overview]: PageInfoOverviewItemSchema,
    [PageSystemPropertyId.FollowupEmailStatus]:
      PageInfoFollowupEmailStatusItemSchema,
    [PageSystemPropertyId.AugmentationStatus]:
      PageInfoAugmentationStatusItemSchema,
    [PageSystemPropertyId.SocialFeed]: PageInfoSocialFeedItemSchema,
    [PageSystemPropertyId.MeetingPrepSummary]:
      PageInfoMeetingPrepSummaryItemSchema,
    [PageSystemPropertyId.PendingAugmentation]:
      PageInfoPendingAugmentationItemSchema,
  }),
});

export const WorkspaceAffinePropertiesSchema = z.object({
  schema: WorkspaceAffinePropertiesSchemaSchema.optional(),
  favorites: z.record(WorkspaceFavoriteItemSchema).optional(),
  pageProperties: z.record(WorkspacePagePropertiesSchema).optional(),
  favoritesMigrated: z.boolean().optional(),
});

export type PageInfoCustomPropertyMeta = z.infer<
  typeof PageCustomPropertyMetaSchema
>;

export type WorkspaceAffineProperties = z.infer<
  typeof WorkspaceAffinePropertiesSchema
>;

export type PageInfoCustomProperty = z.infer<
  typeof PageInfoCustomPropertyItemSchema
>;

export type WorkspaceAffinePageProperties = z.infer<
  typeof WorkspacePagePropertiesSchema
>;

// Query schemas for ActionItemsService
export const RangeFilterSchema = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
  eq: z.number().optional(),
});

export const DateRangeFilterSchema = z.object({
  before: z.union([z.string(), z.date()]).optional(),
  after: z.union([z.string(), z.date()]).optional(),
  eq: z.union([z.string(), z.date()]).optional(),
});

export const DateTimeRangeFilterSchema = z.object({
  before: z.date().optional(),
  after: z.date().optional(),
  exists: z.boolean().optional(),
});

export const ActionItemWhereSchema = z.object({
  id: z.union([z.string(), z.array(z.string())]).optional(),
  title: z.string().optional(),
  pageId: z.union([z.string(), z.array(z.string())]).optional(),
  pageTitle: z.string().optional(),
  status: z
    .union([ActionItemStatusSchema, z.array(ActionItemStatusSchema)])
    .optional(),
  priority: z
    .union([ActionItemPrioritySchema, z.array(ActionItemPrioritySchema)])
    .optional(),
  priorityRank: RangeFilterSchema.optional(),
  progress: RangeFilterSchema.optional(),
  assignedTo: z.array(z.string()).optional(),
  dueDate: DateRangeFilterSchema.optional(),
  createdAt: DateTimeRangeFilterSchema.optional(),
  completedAt: DateTimeRangeFilterSchema.optional(),
  completed: z.boolean().optional(),
  deleted: z.boolean().optional(),
  isFocused: z.boolean().optional(),
});

export const ActionItemOrderBySchema = z.object({
  field: z.enum([
    'id',
    'title',
    'pageId',
    'pageTitle',
    'status',
    'priority',
    'priorityRank',
    'progress',
    'createdAt',
    'dueDate',
    'completedAt',
    'deletedAt',
  ]),
  direction: z.enum(['asc', 'desc']),
});

export type ActionItemGroupBy = keyof z.infer<typeof TodoItemSchema>;
export const ActionItemGroupBySchema: z.ZodType<ActionItemGroupBy> =
  z.custom<ActionItemGroupBy>(
    value => {
      if (typeof value !== 'string') return false;
      return Object.prototype.hasOwnProperty.call(
        (TodoItemSchema as unknown as { shape: Record<string, unknown> }).shape,
        value
      );
    },
    {
      message: 'Invalid groupBy field for action items',
    }
  );

export const ActionItemQuerySchema = z.object({
  where: ActionItemWhereSchema.optional(),
  orderBy: z.array(ActionItemOrderBySchema).optional(),
  groupBy: ActionItemGroupBySchema.optional(),
  limit: z.number().int().min(1).optional(),
  offset: z.number().int().min(0).optional(),
});

export const AggregationTypeSchema = z.enum(['count']);

export type BreakdownStep = z.infer<typeof BreakdownStepSchema>;
export type SerializedTodoItem = z.infer<typeof TodoItemSchema>;
export type SerializedAttendee = z.infer<typeof AttendeeSchema>;
export type SerializedTranscriptionChunk = z.infer<
  typeof TranscriptionChunkSchema
>;
export type SerializedSummaryChunk = z.infer<typeof SummaryChunkSchema>;
export type PageInfoActionItemsItem = z.infer<
  typeof PageInfoActionItemsItemSchema
>;
export type PageInfoFollowupEmailStatusItem = z.infer<
  typeof PageInfoFollowupEmailStatusItemSchema
>;
export type PageInfoAugmentationStatusItem = z.infer<
  typeof PageInfoAugmentationStatusItemSchema
>;
export type PageInfoOverviewItem = z.infer<typeof PageInfoOverviewItemSchema>;
export type ActionItemQuery = z.infer<typeof ActionItemQuerySchema>;
export type ActionItemWhere = z.infer<typeof ActionItemWhereSchema>;
export type AggregationType = z.infer<typeof AggregationTypeSchema>;
export type SerializedSocialFeedItem = z.infer<typeof SocialFeedItemSchema>;
export type MeetingPrepSummaryValue = z.infer<
  typeof MeetingPrepSummaryValueSchema
>;
